/**
 * Converts URL parameters to filter object format
 * This utility handles common URL parameter conversions like:
 * - 'true'/'false' strings to boolean values
 * - Filtering out pagination parameters (page, size)
 * - Removing undefined/null values
 */
import type { QueryParamConfigMap } from '../hooks/query-sync';

/**
 * Generic function to map URL parameters to API format based on configuration
 * This is a reusable utility that transforms parameters according to their defined types
 *
 * @param params - Raw URL parameters (from query string parsing)
 * @param config - Configuration map defining how to transform each parameter
 * @param defaults - Optional default values to merge into the result
 * @returns Transformed parameters ready for API consumption
 */
export function mapUrlParamsToApi<T extends Record<string, unknown>>(
    params: Record<string, unknown>,
    config: QueryParamConfigMap,
    defaults: Partial<T> = {},
): T {
    const apiParams = { ...defaults } as T;

    // Process all parameters generically based on the provided config
    Object.entries(params).forEach(([key, value]) => {
        const paramConfig = config[key];
        if (!paramConfig || value === undefined) {
            return; // Skip undefined values and unconfigured parameters
        }

        switch (paramConfig.type) {
            case 'string':
                if (Array.isArray(value) && value.length > 0) {
                    // For string type, take first non-empty value from array
                    const firstValidValue = value.find(v => v && v !== '');
                    if (firstValidValue) {
                        (apiParams as any)[key] = String(firstValidValue);
                    }
                } else if (value) {
                    (apiParams as any)[key] = String(value);
                }
                break;

            case 'number': {
                const numValue = Number(value);
                if (!Number.isNaN(numValue)) {
                    (apiParams as any)[key] = numValue;
                }
                break;
            }

            case 'boolean':
                (apiParams as any)[key] = Boolean(value);
                break;

            case 'array':
                if (Array.isArray(value)) {
                    if (value.length > 0) {
                        // Apply parser to each array element if provided
                        if (paramConfig.parser) {
                            (apiParams as any)[key] = value.map(v => paramConfig.parser!(String(v)));
                        } else {
                            // Keep elements as strings if no parser provided
                            (apiParams as any)[key] = value;
                        }
                    } else {
                        (apiParams as any)[key] = [];
                    }
                }
                break;
        }
    });

    return apiParams;
}

export function convertUrlParamsToFilters<T extends Record<string, unknown>>(urlParams: Record<string, unknown>): T {
    const filters: Record<string, unknown> = {};

    // Map URL params to filter format
    for (const [key, value] of Object.entries(urlParams)) {
        if (key !== 'page' && key !== 'size' && value !== undefined && value !== null) {
            if (value === 'true') {
                filters[key] = true;
            } else if (value === 'false') {
                filters[key] = false;
            } else {
                filters[key] = value;
            }
        }
    }

    return filters as T;
}

/**
 * Checks if a filter value has meaningful content
 * Returns true if the value is not empty, null, undefined, or an empty array
 */
export function hasFilterValue(value: unknown): boolean {
    return value !== undefined && value !== null && (Array.isArray(value) ? value.length > 0 : true);
}

/**
 * Counts the number of active filters
 * Useful for displaying filter counts in badges
 */
export function countActiveFilters(filters: Record<string, unknown>): number {
    return Object.keys(filters).filter(key => {
        const value = filters[key];
        return hasFilterValue(value);
    }).length;
}
